import { showConfirmModal } from '@components/ConfirmModal';
import Image from '@components/Image';
import { THUMB_DOMAIN } from '@constants';
import { useTabMain } from '@hooks/paramsHooks';
import useToggle from '@hooks/useToggle';
import {
  $curSceneId,
  $curSceneParentId,
  $sceneMap,
  $sceneMapApi,
} from '@stores/dataStores';
import { $panelApi } from '@stores/panelStores';
import {
  DndType,
  type DragItem,
  type GeneralID,
  ImageType,
  type SceneData,
} from '@types';
import addComponent from '@utils/addComponent';
import classNames from 'classnames';
import { useStore, useStoreMap } from 'effector-react';
import React, { useRef } from 'react';
import { DragPreviewImage, useDrag, useDrop } from 'react-dnd';
import MoreButton from '../MoreButton';
import { showScenesCopyModal } from '../ScenesCopyModal';
import { changeThumb } from '../actions/changeThumb';
import { deleteScene } from '../actions/deleteActions';
import duplicateScene from '../actions/duplicateScene';
import { renameScene } from '../actions/renameScene';
import replaceScene from '../actions/replaceScene';
import { selectScene } from '../actions/selectActions';
import { sortScene } from '../actions/sortActions';
import RenameSceneModal from './RenameSceneModal';
import ThirdPartyModal from './ThirdPartyModal';
import TypeMark from './TypeMark';

interface Props {
  id: GeneralID;
  index: number;
}

const previewUrl = (src: string) =>
  `${THUMB_DOMAIN}/${src}?x-oss-process=image/auto-orient,1/resize,h_40,w_40,m_fill`;

function SceneItem({ id, index }: Props) {
  const tab = useTabMain();
  const ref = useRef<HTMLDivElement>(null);
  const isActive = useStore($curSceneId) === id;
  const data = useStoreMap({
    store: $sceneMap,
    keys: [id],
    fn: (state, [_id]) => state.get(_id) || ({} as SceneData),
  });
  const { thumbUrl, name, type } = data;
  const loading = useToggle(false);

  const [{ isDragging }, drag, preview] = useDrag({
    type: DndType.scene,
    item: { id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: DndType.scene,
    hover(item: DragItem, monitor) {
      if (!ref.current) return;
      const fromIndex = item.index;
      const toIndex = index;
      if (fromIndex === toIndex) return;
      const rect = ref.current.getBoundingClientRect();
      const hoverMiddleX = (rect.right - rect.left) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientX = clientOffset!.x - rect.left;
      if (fromIndex < toIndex && hoverClientX < hoverMiddleX) return;
      if (fromIndex > toIndex && hoverClientX > hoverMiddleX) return;
      sortScene({ fromIndex, toIndex });
      item.index = toIndex;
    },
  });

  drop(drag(ref));

  const handleDelete = async () => {
    $panelApi.dismiss();
    const { confirmed } = await showConfirmModal({
      title: '确认删除场景',
      content: `场景${name}下所有设置也都将会被删除，确认删除？`,
    });
    if (confirmed) {
      deleteScene([{ sceneId: id, categoryId: $curSceneParentId.getState() }]);
    }
  };

  const handleRename = async () => {
    addComponent((remove) => (
      <RenameSceneModal
        isOpen
        oldName={name}
        onRequestClose={remove}
        onConfirm={(result) => {
          renameScene({
            sceneId: id,
            name: result.name,
            renameHotspot: result.renameHotspot,
            renameSandTable: result.renameSandTable,
          });
          remove();
        }}
      />
    ));
  };

  const handleChangeThirdPartyId = async () => {
    addComponent((remove) => (
      <ThirdPartyModal
        oldId={data.config.partnerId}
        isOpen
        onRequestClose={remove}
        onConfirm={(result) => {
          $sceneMapApi.update((draft) => {
            draft.get(id)!.config.partnerId = result.id;
          });
          remove();
        }}
      />
    ));
  };

  return (
    <div
      ref={ref}
      className={classNames(
        'relative ml-10 h-80 w-80 flex-shrink-0 rounded border-2 border-transparent focus:outline-none',
        {
          'opacity-0': isDragging,
        }
      )}
      onClick={() => isActive || selectScene(id)}
    >
      <DragPreviewImage connect={preview} src={previewUrl(thumbUrl)} />
      <Image
        className={classNames('h-full w-full rounded', {
          'ring-2 ring-orange': isActive,
        })}
        src={thumbUrl}
        type={ImageType.thumb}
        size={80}
      />
      <div
        className="absolute bottom-0 left-0 right-0 truncate rounded-b bg-black-black bg-opacity-50 px-2 text-center text-sm"
        title={name}
      >
        {name}
      </div>
      <TypeMark data={data} tab={tab} type={type} />
      <MoreButton
        className="absolute top-4 right-4 rounded bg-black-black bg-opacity-50 hover:bg-blue hover:opacity-100"
        items={
          [
            '重新命名',
            '修改封面',
            '复制场景',
            '替换场景',
            '删除场景',
            '跨作品复制',
            '绑定第3方ID',
          ] as const
        }
        onSelect={async (val) => {
          if (val === '删除场景') {
            await handleDelete();
          } else if (val === '重新命名') {
            await handleRename();
          } else if (val === '修改封面') {
            await changeThumb(data);
          } else if (val === '替换场景') {
            loading.on();
            await replaceScene({ sceneType: data.type, id });
            loading.off();
          } else if (val === '复制场景') {
            duplicateScene({ sceneId: id });
          } else if (val === '跨作品复制') {
            showScenesCopyModal({ level: '场景', sceneIds: [id] });
          } else if (val === '绑定第3方ID') {
            await handleChangeThirdPartyId();
          }
        }}
      />
      {loading.isOn && (
        <div className="absolute inset-0 flex items-center justify-center bg-black-black bg-opacity-70 text-sm">
          加载中…
        </div>
      )}
    </div>
  );
}

export default React.memo(SceneItem);
